import { typographyTokens } from '../../theme/typographyTokens';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link } from 'react-router-dom';
import {
  TaskExtended,
  TaskTemplate,
  TaskStatus,
  Task,
  ReadableTaskStatus,
  defaultSystemTask
} from '../../../shared/types/Task';
import { CommentExtended } from '../../../shared/types/Comment';
import StatusCell from '../SepTable/StatusCell';
import './style.css';
import { Accordion, AccordionSummary, AccordionDetails, Chip, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetAttachmentByIDQuery } from '../../services/attachmentSlice/attachmentSlice';
import { useGetCommentsQuery } from '../../services/commentSlice/commentSlice';
import ModalWidget from '../ModalWidget/ModalWidget';
import DependentTasksTable from './DependentTasksTable';
import { useGetDepartmentsQuery } from '../../services/departmentSlice/departmentSlice';
import { useAccount } from '@azure/msal-react';
import { useGetTaskTemplatesSLAQuery } from '../../services/tasksSlice/tasksSlice';
import { useGetActivitiesQuery } from '../../services/activitySlice/activitySlice';
import { getEscalationDate } from '../../utils/EscalationDatetaskUtils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import getTaskAge from '../../../frontend/utils/taskAgeCalculation';
import OnTheClockIcon from '../../assets/svg/OnTheClockIcon';
import CurrentPhaseIcon from '../../assets/svg/CurrentPhaseIcon';
import DepartmentIconRenderer from '../../assets/svg/departmentIconMap';
import AttachmentListIcon from '../../assets/svg/AttachmentListIcon';
import CommentListIcon from '../../assets/svg/CommentListIcon';
import { newUiColorTokens } from '../../theme/themeTokens';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.gray.light,
    fontSize: typographyTokens.fontSizes.body,
    paddingBottom: 4,
    padding: '8px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover > td': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td:not(.department-cell), &:last-child th:not(.department-cell)': {
    border: 0,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.gray.main,
  '&:hover': {
    color: theme.palette.solidBlue.main,
  },
}));

const TaskInfoCell = ({
  task,
  comments,
  sepID,
  taskID,
  onShowDependentList,
  isMyTask,
  superUser,
}: {
  task: TaskExtended;
  comments: CommentExtended[];
  sepID: number;
  taskID: number;
  onShowDependentList: (tasks: Task[]) => void;
  isMyTask: boolean;
  superUser: boolean;
}) => {
  const pending = task.status === TaskStatus.pending;
  const dependentTasks = task.parentTasks.filter((t) => t.enabled);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const taskComments: CommentExtended[] = superUser
    ? comments.filter((c) => c.taskID === task.id)
    : comments.filter(
      (c) => c.taskID === task.id && !c.departmentID && !c.taskID
    );
  const [hover, setHover] = useState<boolean>(false);
  return (
    <Box
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink to={`/sep/${sepID}/task/${taskID}`}>
        <Typography
          sx={{
            float: 'left',
            minWidth: '16px',
            maxWidth: '16px',
            minHeight: '4px',
          }}
        >
          {isMyTask ? (
            <i
              style={{
                fontSize: typographyTokens.fontSizes.caption,
                color:
                  task.status === TaskStatus.complete && !hover
                    ? theme.palette.prime.green
                    : theme.palette.prime.blue,
              }}
              className={
                hover
                  ? 'fas fa-sign-in-alt'
                  : task.status === TaskStatus.complete
                    ? 'fa-solid fa-check'
                    : 'fa-solid fa-circle-small'
              }
            />
          ) : (
            <i
              style={{
                fontSize: typographyTokens.fontSizes.caption,
                color:
                  task.status === TaskStatus.complete && !hover
                    ? theme.palette.prime.green
                    : theme.palette.prime.blue,
              }}
              className={
                hover
                  ? 'fas fa-sign-in-alt'
                  : task.status === TaskStatus.complete
                    ? 'fa-solid fa-check'
                    : ''
              }
            />
          )}
        </Typography>
        <Typography color={isDarkMode ? theme.palette.content.white : theme.palette.prime.blue}>{task.name}</Typography>
      </StyledLink>
      <Box sx={{ display: 'flex', marginTop: '8px', marginLeft: '16px' }}>
        {pending && dependentTasks.length > 0 && (
          <>
            <Typography
              sx={{
                marginTop: '3px',
                marginRight: '16px',
                height: '8px',
                width: '8px',
                border: '1px solid gray',
                borderTop: '0px',
                borderRight: '0px',
              }}
            ></Typography>
            <em className="normal-text">
              This task is dependent on{' '}
              <span className="blue-text">
                <em
                    className="blue-text"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onShowDependentList(dependentTasks)}
                  >
                    {`${dependentTasks.length} other tasks `}
                  </em>
              </span>
              {dependentTasks.length === 1 && (
                <>
                  {` that is currently in `}
                  <b>
                    {dependentTasks[0].status === TaskStatus.pending
                      ? 'Locked'
                      : ReadableTaskStatus[dependentTasks[0].status]}
                  </b>
                  {' status.'}
                </>
              )}
              {dependentTasks.length > 1 && (
                <>that are in various other statuses.</>
              )}
            </em>
          </>
        )}
        {pending && dependentTasks.length === 0 && <></>}
        {!pending && taskComments.length > 0 && (
          <>
            <Typography
              sx={{
                marginTop: '3px',
                marginRight: '16px',
                height: '8px',
                width: '8px',
                border: '1px solid gray',
                borderTop: '0px',
                borderRight: '0px',
              }}
            ></Typography>
            <em className="normal-text">Latest Comment:</em>
            <span
              className="blue-text"
              style={{ width: '250px', marginLeft: '8px' }}
            >
              <StyledLink
                to={{
                  pathname: `/sep/${sepID}/task/${taskID}`,
                }}
                className="blue-text"
                dangerouslySetInnerHTML={{
                  __html: taskComments[taskComments.length - 1].comment,
                }}
              ></StyledLink>
            </span>
          </>
        )}
        {!pending && taskComments.length === 0 && <></>}
      </Box>
    </Box>
  );
};

interface IconCellProps {
  icon?: string;
  iconHover?: string;
  iconComponent?: React.ReactNode;
  value: string;
  taskID?: number;
  sepID?: number;
  linkTo?: string;
  state?: any;
}

export const IconCell = ({ icon, iconHover, iconComponent, value, linkTo, state }: IconCellProps) => {
  const [className, setClassName] = useState(icon);
  const content = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {iconComponent || <i className={className}></i>}
      {value}
    </Box>
  );
  return (
    <div
      onMouseOver={() => iconHover && setClassName(iconHover)}
      onMouseLeave={() => setClassName(icon)}
      style={{ cursor: linkTo ? 'pointer' : 'default' }}
    >
      {linkTo ? <Link to={linkTo} state={state} style={{ color: 'inherit', textDecoration: 'none' }}>{content}</Link> : content}
    </div>
  );
};

interface TaskListTableProps {
  tasks: TaskExtended[];
  sepID: number;
  noBorder?: boolean;
  isMyTask?: boolean;
  sepPhase?: string;
}

export default function TaskListTable({
  tasks,
  sepID,
  noBorder,
  isMyTask = false,
  sepPhase,
}: TaskListTableProps) {
  const theme = useTheme();
  const { data: attachments } = useGetAttachmentByIDQuery(sepID);
  const { data: comments } = useGetCommentsQuery(sepID);
  const { data: departments } = useGetDepartmentsQuery();
  const { data: taskTemplatesSLA } = useGetTaskTemplatesSLAQuery();
  const { data: activities } = useGetActivitiesQuery({ sepID: sepID.toString(), trackableType: 'Task' });
  const [showModal, setShowModal] = useState<Task[]>([]);
  const phases = ['validation', 'initiate', 'design', 'implement'];
  const isDarkMode = theme.palette.mode === 'dark';

  // Refs and state for sticky horizontal scrollbar
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const isScrolling = useRef(false);

  // Sync scroll width and handle scroll synchronization
  useEffect(() => {
    const updateScrollWidth = () => {
      if (tableContainerRef.current) {
        setScrollWidth(tableContainerRef.current.scrollWidth);
      }
    };

    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(updateScrollWidth);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateScrollWidth);
      resizeObserver.disconnect();
    };
  }, [tasks]);

  const handleTableScroll = () => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    if (stickyScrollbarRef.current && tableContainerRef.current) {
      stickyScrollbarRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
    isScrolling.current = false;
  };

  const handleStickyScroll = () => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    if (tableContainerRef.current && stickyScrollbarRef.current) {
      tableContainerRef.current.scrollLeft = stickyScrollbarRef.current.scrollLeft;
    }
    isScrolling.current = false;
  };

  const taskTemplates = useMemo(
    () => (taskTemplatesSLA || []).map((template) => ({
      ...template,
      taskAge: template.requestorSla1,
      stakeholderSla: template.stakeholderSla1
        ? JSON.stringify({ stakeholderSla1: template.stakeholderSla1 })
        : null,
    })),
    [taskTemplatesSLA]
  );

  const taskOrderByTemplateId = useMemo(
    () => new Map(taskTemplates.map((template) => [template.id, template.taskOrderId])),
    [taskTemplates]
  );

  const groupedTasks: { [key: string]: TaskExtended[] } = {};
  phases.forEach((phase) => {
    groupedTasks[phase] = tasks
      .filter((task) => task.phase === phase)
      .sort((a, b) => {
        // Sort by department display order, then template task order.
        const deptA = a.department?.name || '';
        const deptB = b.department?.name || '';
        const departmentOrderA = a.department?.departmentDisplayOrderId;
        const departmentOrderB = b.department?.departmentDisplayOrderId;
        const hasDepartmentOrderA = departmentOrderA !== null && departmentOrderA !== undefined;
        const hasDepartmentOrderB = departmentOrderB !== null && departmentOrderB !== undefined;

        if (a.departmentID !== b.departmentID) {
          if (hasDepartmentOrderA && hasDepartmentOrderB && departmentOrderA !== departmentOrderB) {
            return departmentOrderA - departmentOrderB;
          }
          if (hasDepartmentOrderA !== hasDepartmentOrderB) {
            return hasDepartmentOrderA ? -1 : 1;
          }
          // Keep different departments together, using the name to resolve
          // equal display orders or departments without a display order.
          return deptA.localeCompare(deptB);
        }

        const hasTaskTemplateA = a.taskTemplateID !== null && a.taskTemplateID !== undefined;
        const hasTaskTemplateB = b.taskTemplateID !== null && b.taskTemplateID !== undefined;

        if (hasTaskTemplateA !== hasTaskTemplateB) {
          return hasTaskTemplateA ? 1 : -1;
        }

        if (!hasTaskTemplateA) {
          return a.name.localeCompare(b.name);
        }

        const taskOrderA = a.taskTemplateID !== undefined
          ? taskOrderByTemplateId.get(a.taskTemplateID)
          : null;
        const taskOrderB = b.taskTemplateID !== undefined
          ? taskOrderByTemplateId.get(b.taskTemplateID)
          : null;
        const hasTaskOrderA = taskOrderA !== null && taskOrderA !== undefined;
        const hasTaskOrderB = taskOrderB !== null && taskOrderB !== undefined;

        if (hasTaskOrderA && hasTaskOrderB && taskOrderA !== taskOrderB) {
          return taskOrderA - taskOrderB;
        }
        if (hasTaskOrderA !== hasTaskOrderB) {
          return hasTaskOrderA ? -1 : 1;
        }

        return a.name.localeCompare(b.name);
      });
  });

  // Helper function to calculate rowSpan for department grouping
  const getDepartmentRowSpan = (tasksInPhase: TaskExtended[], index: number): number | null => {
    const currentDept = tasksInPhase[index].department?.name || '';
    const currentReviewer = tasksInPhase[index].defaultReviewer?.displayName || '';
    const currentKey = `${currentDept}-${currentReviewer}`;

    // Check if this is the first row of a department group
    if (index > 0) {
      const prevDept = tasksInPhase[index - 1].department?.name || '';
      const prevReviewer = tasksInPhase[index - 1].defaultReviewer?.displayName || '';
      const prevKey = `${prevDept}-${prevReviewer}`;
      if (currentKey === prevKey) {
        return null; // Not the first row, don't render cell
      }
    }

    // Count consecutive rows with the same department
    let count = 1;
    for (let i = index + 1; i < tasksInPhase.length; i++) {
      const nextDept = tasksInPhase[i].department?.name || '';
      const nextReviewer = tasksInPhase[i].defaultReviewer?.displayName || '';
      const nextKey = `${nextDept}-${nextReviewer}`;
      if (nextKey === currentKey) {
        count++;
      } else {
        break;
      }
    }
    return count;
  };
  const getBackgroundColor = (phase: string) => {
    switch (phase) {
      case 'validation':
        return 'rgba(117, 141, 163, 1)';
      case 'initiate':
        return 'rgba(65, 105, 225, 1)'; // Transparent blue
      case 'design':
        return 'rgba(255, 165, 0, 0.6)';
      case 'implement':
        return 'rgba(0, 128, 0, 0.6)';
      default:
        return 'transparent';
    }
  };
  const getTextColor = (phase: string) => {
    switch (phase) {
      case 'validation':
        return 'light blue-gray';
      case 'initiate':
        return 'blue';
      case 'design':
        return 'orange';
      case 'implement':
        return 'green';
      default:
        return '0px'; // Default text color
    }
  };

  const getBrRadius = (phase: string) => {
    switch (phase) {
      case 'validation':
        return 'lightgrey';
      case 'initiate':
        return 'lightgrey';
      case 'design':
        return 'lightgrey';
      case 'implement':
        return 'lightgrey';
      default:
        return 'transparent';
    }
  };

  const account = useAccount();
  const roleList: any = useMemo(() => {
    return account?.idTokenClaims?.roles;
  }, [account]);
  const superUser =
    roleList?.includes('AuthSuperUser') || roleList?.includes('AuthLegal');

  if (tasks.length === 0) {
    return (
      <Typography pl={4} py={4} sx={{ font: 'normal normal normal 14px/22px Open Sans;', color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
        No current tasks at this time.
      </Typography>
    );
  }
  return (
    <>
      <ModalWidget
        isOpen={!!showModal.length}
        title="All Dependent Tasks"
        styleObject={{ width: 'fit-content', minWidth: '600px', color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}
        children={
          <DependentTasksTable
            task={showModal}
            departments={departments || []}
            attachments={attachments || []}
            comments={comments || []}
          />
        }
        onClose={() => setShowModal([])}
      />
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TableContainer
          ref={tableContainerRef}
          sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'visible',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            mb: 4,
          }}
          onScroll={handleTableScroll}
        >
          {phases.map((phase) => {
            const tasksInPhase = groupedTasks[phase];

            if (tasksInPhase.length === 0) {
              return null;
            }

            return (
              <Accordion
                key={phase}
                defaultExpanded
                sx={{
                  width: '100%',
                  minWidth: '100%',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  '&.Mui-expanded': { margin: 0 }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${phase}-content`}
                  id={`${phase}-header`}
                  sx={{
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'
                    },
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {sepPhase === phase && <CurrentPhaseIcon circleColor={newUiColorTokens.prime.blue} />}
                    <Typography variant="h6" color={isDarkMode ? theme.palette.content.white : theme.palette.content.default} sx={{ fontWeight: typographyTokens.fontWeights.bold }}>
                      {phase.charAt(0).toUpperCase() + phase.slice(1)} Phase
                    </Typography>
                    {(() => {
                      const myActiveTasksCount = tasksInPhase.filter(
                        (task) =>
                          account?.localAccountId === task?.assignee?.id &&
                          (task.status === TaskStatus.todo ||
                            task.status === TaskStatus.inReview ||
                            task.status === TaskStatus.changesRequested)
                      ).length;
                      return myActiveTasksCount > 0 ? (
                        <Chip
                          label={`You're on the clock for ${myActiveTasksCount} ${myActiveTasksCount === 1 ? 'task' : 'tasks'}`}
                          size="small"
                          icon={<OnTheClockIcon color={theme.palette.content.white} />}
                          sx={{
                            fontWeight: typographyTokens.fontWeights.medium,
                            py: 2,
                            px: 1,
                            ml: 1,
                            color: theme.palette.content.white,
                            backgroundColor: theme.palette.prime.orange,
                            fontSize: typographyTokens.fontSizes.caption,
                          }}
                        />
                      ) : null;
                    })()}
                  </Box>
                  <Chip
                    label={`${tasksInPhase.filter(t => t.status === TaskStatus.complete || t.status === TaskStatus.cancelled).length}/${tasksInPhase.length} Complete`}
                    size="small"
                    variant="outlined"
                    sx={{
                      mr: 2,
                      borderColor: theme.palette.prime.granite,
                      color: isDarkMode ? theme.palette.content.white : theme.palette.content.default,
                      fontSize: typographyTokens.fontSizes.caption,
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Table
                    stickyHeader
                    sx={{
                      width: '100%',
                      minWidth: 650,
                      tableLayout: 'fixed',
                      borderBottom: `2px solid ${newUiColorTokens.elements.divider}`,
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.06)'
                            : newUiColorTokens.surface.level1,
                      },
                    }}
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: '15%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center">Review Department</StyledTableCell>
                        <StyledTableCell sx={{ width: '35%', textAlign: 'left', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="left">Task</StyledTableCell>
                        <StyledTableCell sx={{ width: '6%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center">Status</StyledTableCell>
                        <StyledTableCell sx={{ width: '12%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center">On the Clock</StyledTableCell>
                        <StyledTableCell sx={{ width: '7%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center" colSpan={1}>Age (Days)</StyledTableCell>
                        <StyledTableCell sx={{ width: '11%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center">Task Last Accessed</StyledTableCell>
                        <StyledTableCell sx={{ width: '7%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center" colSpan={2}>Activity</StyledTableCell>
                        <StyledTableCell sx={{ width: '7%', textAlign: 'center', fontSize: typographyTokens.fontSizes.base, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} align="center">Due Date</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedTasks[phase].map((row, i) => {
                        const { activeDays, color, ageTextColor, userAssigmentDetails } = getTaskAge(row, taskTemplates, theme);
                        const departmentRowSpan = getDepartmentRowSpan(groupedTasks[phase], i);
                        const isAssignedToCurrentUser = account?.localAccountId === row?.assignee?.id;

                        // Get latest activity for this task
                        const taskActivities = activities?.activities?.filter(
                          (a) => a.trackableType === 'Task' && a.trackableID === row.id
                        ) || [];
                        const latestActivity = taskActivities.length > 0
                          ? taskActivities.reduce((latest, current) =>
                            new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
                          )
                          : null;

                        return (
                          <StyledTableRow
                            key={row.name + i}
                          >
                            {departmentRowSpan !== null && (
                              <StyledTableCell
                                className="department-cell"
                                align="center"
                                rowSpan={departmentRowSpan}
                                sx={{
                                  verticalAlign: 'middle',
                                  borderRight: '1px solid',
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  backgroundColor: isDarkMode ? 'inherit' : 'rgba(250, 250, 250, 1)'
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                                  <DepartmentIconRenderer
                                    departmentName={row?.department?.name || ''}
                                    width={18}
                                    height={18}
                                    color={isDarkMode ? theme.palette.content.white : theme.palette.content.default}
                                  />
                                  <Typography variant="body2" fontWeight="medium" color={isDarkMode ? theme.palette.content.white : theme.palette.content.default}>
                                    {row?.department?.name}
                                  </Typography>
                                </Box>
                                {row.defaultReviewer?.displayName && (
                                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: typographyTokens.fontSizes.caption, color: isDarkMode ? theme.palette.gray.light : theme.palette.gray.darkGray1 }}>
                                    {row.defaultReviewer?.displayName}
                                  </Typography>
                                )}
                              </StyledTableCell>
                            )}
                            <StyledTableCell
                              sx={{
                                paddingRight: '40px',
                                background: !isDarkMode && isAssignedToCurrentUser
                                  ? 'linear-gradient(90deg, rgba(239, 108, 0, 0.05) 0%, rgba(239, 108, 0, 0.00) 58.65%)'
                                  : 'none',
                              }}
                              component="th"
                              scope="row"
                            >
                              <TaskInfoCell
                                task={row}
                                comments={comments || []}
                                sepID={sepID}
                                taskID={row.id}
                                onShowDependentList={(tasks) => {
                                  setShowModal(tasks);
                                }}
                                isMyTask={!!isMyTask}
                                superUser={superUser}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <StatusCell status={row.status} />
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {row?.defaultAssignee === defaultSystemTask?.systemTask
                                ? defaultSystemTask?.systemTrackingTask
                                : isAssignedToCurrentUser ? (
                                  <Chip
                                    label="You"
                                    size="small"
                                    color="primary"
                                    icon={row?.status === TaskStatus.todo || row?.status === TaskStatus.inReview || row?.status === TaskStatus.changesRequested ? <OnTheClockIcon color={theme.palette.content.white} /> : undefined}
                                    sx={{ fontWeight: typographyTokens.fontWeights.medium, py: 2, px: 1, display: 'inline-flex', alignItems: 'center', color: theme.palette.content.white, backgroundColor: row?.status === TaskStatus.todo || row?.status === TaskStatus.inReview || row?.status === TaskStatus.changesRequested ? theme.palette.prime.orange : theme.palette.prime.granite, fontSize: typographyTokens.fontSizes.caption }}
                                  />
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                                    {(row?.status === TaskStatus.todo || row?.status === TaskStatus.inReview || row?.status === TaskStatus.changesRequested) && <OnTheClockIcon color={theme.palette.prime.blue} style={{ marginRight: '4px' }} />} {row?.assignee?.givenName} {row?.assignee?.surname}
                                  </Box>
                                )
                              }
                            </StyledTableCell>
                            <StyledTableCell align="center" sx={{ px: 4}}>
                              <div style={{ background: color, color: ageTextColor, display: "flex", justifyContent: 'center', alignItems: 'center', padding: '4px 12px', borderRadius: '4px' }}>
                                {activeDays}
                                <span>
                                  {userAssigmentDetails && userAssigmentDetails.length > 0 && (
                                    <Tooltip
                                      style={{ marginLeft: '3px' }}
                                      title={
                                        <span
                                          style={{
                                            fontSize: typographyTokens.fontSizes.lg,
                                            padding: '16px 8px 12px 8px',
                                            maxHeight: '70vh',
                                            overflowY: 'auto',
                                            display: 'block',
                                          }}
                                        >
                                          <b>User Assignment Details</b><br />
                                          {userAssigmentDetails.map((period, index) => (
                                            <>
                                              <br />
                                              <table
                                                key={index}
                                                style={{
                                                  marginBottom: '12px',
                                                  borderCollapse: 'collapse',
                                                  width: '100%',
                                                }}
                                              >
                                                <tbody>
                                                  <tr>
                                                    <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                                      <b>User:</b> {period.userId}
                                                    </td>
                                                  </tr>
                                                  <tr>
                                                    <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                                      <b>Time:</b> {period.days ?? 'NA'} days, {period.hours ?? 'NA'} hours, {period.mins ?? 'NA'} mins
                                                    </td>
                                                  </tr>
                                                  <tr>
                                                    <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                                      <b>From:</b> {period.from ? period.from.toLocaleString() : 'NA'}
                                                    </td>
                                                  </tr>
                                                  <tr>
                                                    <td style={{ padding: '4px' }}>
                                                      <b>To:</b> {period.to ? period.to.toLocaleString() : 'NA'}
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </>
                                          ))}
                                        </span>
                                      }
                                      placement="top"
                                      arrow
                                    >
                                      <IconButton size="small" style={{ color: ageTextColor ? ageTextColor : theme.palette.gray.darkGray1 }}>
                                        <InfoOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </span>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {latestActivity ? (
                                <Tooltip
                                  title={latestActivity.title || 'No title'}
                                  placement="top"
                                  arrow
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <IconButton size="small" sx={{ color: theme.palette.primary.main }}>
                                      <HistoryIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ fontSize: typographyTokens.fontSizes.caption, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                                      {new Date(latestActivity.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                ''
                              )}
                            </StyledTableCell>
                            <StyledTableCell align="right" sx={{ px: 1 }}>
                              <IconCell
                                iconComponent={<CommentListIcon color={theme.palette.prime.blue} />}
                                value={
                                  comments
                                    ?.filter((e) => e.taskID === row.id)
                                    ?.length.toString() || '0'
                                }
                                linkTo={`/sep/${sepID}/task/${row.id}`}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="left" sx={{ px: 0.75 }}>
                              <IconCell
                                iconComponent={<AttachmentListIcon color={theme.palette.prime.blue} />}
                                value={
                                  attachments
                                    ?.filter((e) => e.taskID === row.id)
                                    ?.length.toString() || '0'
                                }
                                linkTo={`/sep/${sepID}/task/${row.id}`}
                                state={{ activeTab: 'Attachments' }}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="center" sx={{ px: 0.75, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                              {getEscalationDate(row, taskTemplates as unknown as TaskTemplate[])}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ width: '6px', paddingLeft: '5px' }}
                              align="right"
                            ></StyledTableCell>
                          </StyledTableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {/* {renderSeparator(isMyTask)} */}
        </TableContainer>
        {/* Sticky horizontal scrollbar */}
        <Box
          ref={stickyScrollbarRef}
          onScroll={handleStickyScroll}
          sx={{
            position: 'sticky',
            bottom: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            backgroundColor: 'background.paper',
            zIndex: 10,
            '&::-webkit-scrollbar': {
              height: '16px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          <Box sx={{ width: scrollWidth, height: '3px' }} />
        </Box>
      </Box>
    </>
  );
}